import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Label,
  Input,
  FormFeedback,
  Form,
  Spinner,
} from "reactstrap";

import * as Yup from "yup";
import { useFormik } from "formik";
import { indianMobileSchema, PAN_CARD_REGEX } from "../../helpers";
import CommonMobileInput from "../../components/Common/CommonMobileInput";
import CircularImagePicker from "../../components/Common/CircularImagePicker";
import { withTranslation } from "react-i18next";
import withRouter from "../../components/Common/withRouter";
import Breadcrumb from "../../components/Common/Breadcrumb";

import avatar from "../../assets/images/users/avatar-1.jpg";
import { PLATFORM_NAME } from "../../CommonData/Data/constant";
import callApi from "../../helpers/CallApi";
import { updateProfile, getBusinessDetail, updateBusinessDetail } from "../../helpers/API";
import { toast } from "react-toastify";

const EMPTY_BUSINESS_DETAIL = {
  companyName: "",
  email: "",
  mobile: "",
  gstNumber: "",
  panCard: "",
  address: "",
  logo: "",
  subscriptionId: "",
  otherDetails: { registrationNumber: "" },
};

const UserProfile = ({ t }) => {
  document.title = t("document.title.profile", { PLATFORM_NAME });

  const [userState, setUserState] = useState({
    name: "",
    email: "",
    mobile: "",
    profile: "",
    id: null,
    role: "",
    companyId: null,
  });

  const [businessDetail, setBusinessDetail] = useState(EMPTY_BUSINESS_DETAIL);
  const [businessLoading, setBusinessLoading] = useState(false);

  const isDistributor = userState.role === "distributor";

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUserState({
          name: parsedUser?.name || "",
          email: parsedUser?.email || "",
          mobile: parsedUser?.mobile || "",
          profile: parsedUser?.profile || "",
          id: parsedUser?.id || null,
          role: parsedUser?.role || "",
          companyId: parsedUser?.companyId || null,
        });
      } catch (error) {
        console.error("Error parsing user data from localStorage:", error);
      }
    }
  }, []);

  useEffect(() => {
    if (!isDistributor || !userState.companyId) return;

    setBusinessLoading(true);
    callApi({
      callback: getBusinessDetail,
      payload: [],
      onSuccess: (data) => {
        setBusinessDetail({
          companyName: data?.companyName || "",
          email: data?.email || "",
          mobile: data?.mobile || "",
          gstNumber: data?.gstNumber || "",
          panCard: data?.panCard || "",
          address: data?.address || "",
          logo: data?.logo || "",
          subscriptionId: data?.subscriptionId != null ? String(data.subscriptionId) : "",
          otherDetails: {
            registrationNumber: data?.otherDetails?.registrationNumber || "",
          },
        });
        setBusinessLoading(false);
      },
      onError: (error) => {
        toast.error(error);
        setBusinessLoading(false);
      },
    });
  }, [isDistributor, userState.companyId]);

  const getValidationSchema = () => {
    return Yup.object({
      name: Yup.string()
        .min(2, t("profile.form.validation.nameMinLength"))
        .max(50, t("profile.form.validation.nameMaxLength"))
        .required(t("profile.form.validation.nameRequired")),
      email: Yup.string()
        .email(t("profile.form.validation.emailInvalid"))
        .required(t("profile.form.validation.emailRequired")),
      mobile: indianMobileSchema(t, {
        requiredMessage: t("profile.form.validation.mobileRequired"),
        invalidMessage: t("profile.form.validation.mobileInvalid"),
      }),
      profile: Yup.string().nullable(),
    });
  };

  const getBusinessValidationSchema = () => {
    return Yup.object({
      companyName: Yup.string().required(t("profile.businessDetail.validation.shopNameRequired")),
      email: Yup.string()
        .email(t("profile.businessDetail.validation.emailInvalid"))
        .required(t("profile.businessDetail.validation.emailRequired")),
      mobile: indianMobileSchema(t, {
        requiredMessage: t("profile.businessDetail.validation.mobileRequired"),
        invalidMessage: t("profile.businessDetail.validation.mobileInvalid"),
      }),
      gstNumber: Yup.string().required(t("profile.businessDetail.validation.gstRequired")),
      panCard: Yup.string()
        .matches(PAN_CARD_REGEX, t("profile.businessDetail.validation.panCardInvalid"))
        .required(t("profile.businessDetail.validation.panCardRequired")),
      address: Yup.string().nullable(),
      logo: Yup.string().nullable(),
      otherDetails: Yup.object({
        registrationNumber: Yup.string().nullable(),
      }),
    });
  };

  const buildBusinessPayload = (values) => ({
    companyName: values.companyName,
    email: values.email,
    mobile: values.mobile,
    gstNumber: values.gstNumber,
    panCard: (values.panCard || "").toUpperCase(),
    address: values.address,
    logo: values.logo,
    otherDetails: {
      registrationNumber: values.otherDetails?.registrationNumber || "",
    },
  });

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: userState.name,
      email: userState.email,
      mobile: userState.mobile,
      profile: userState.profile,
      id: userState.id,
    },
    validationSchema: getValidationSchema(),
    onSubmit: (values) => {
      const payload = {
        name: values.name,
        email: values.email,
        mobile: values.mobile,
      };
      callApi({
        callback: updateProfile,
        payload,
        onSuccess: ({ message, user }) => {
          toast.success(message);
          localStorage.setItem("user", JSON.stringify(user));
          setUserState({
            name: user.name,
            email: user.email,
            mobile: user.mobile,
            profile: user.profile || "",
            id: user.id,
            role: user.role,
            companyId: user.companyId,
          });
        },
        onError: (error) => {
          toast.error(error);
        },
      });
    },
  });

  const businessValidation = useFormik({
    enableReinitialize: true,
    initialValues: businessDetail,
    validationSchema: getBusinessValidationSchema(),
    onSubmit: (values) => {
      const payload = buildBusinessPayload(values);
      callApi({
        callback: updateBusinessDetail,
        payload,
        onSuccess: ({ message, businessDetail: updated }) => {
          toast.success(message || t("profile.businessDetail.messages.updateSuccess"));
          setBusinessDetail({
            companyName: updated?.companyName || "",
            email: updated?.email || "",
            mobile: updated?.mobile || "",
            gstNumber: updated?.gstNumber || "",
            panCard: updated?.panCard || "",
            address: updated?.address || "",
            logo: updated?.logo || "",
            subscriptionId: updated?.subscriptionId != null ? String(updated.subscriptionId) : "",
            otherDetails: {
              registrationNumber: updated?.otherDetails?.registrationNumber || "",
            },
          });
        },
        onError: (error) => {
          toast.error(error);
        },
      });
    },
  });

  const imageEditorLabels = (section) => ({
    editLabel: t(`profile.${section}.imageEditor.changePhoto`),
    modalTitle: t(`profile.${section}.imageEditor.title`),
    rotateLeftLabel: t(`profile.${section}.imageEditor.rotateLeft`),
    zoomLabel: t(`profile.${section}.imageEditor.zoom`),
    cancelLabel: t(`profile.${section}.imageEditor.cancel`),
    applyLabel: t(`profile.${section}.imageEditor.apply`),
    uploadingLabel: t(`profile.${section}.imageEditor.uploading`),
  });

  const handleProfilePhotoSave = async (profileUrl) => {
    const payload = {
      name: validation.values.name,
      email: validation.values.email,
      mobile: validation.values.mobile,
      profile: profileUrl,
    };

    await new Promise((resolve, reject) => {
      callApi({
        callback: updateProfile,
        payload,
        onSuccess: ({ message, user }) => {
          toast.success(message || t("profile.form.messages.photoUploadSuccess"));
          localStorage.setItem("user", JSON.stringify(user));
          validation.setFieldValue("profile", user.profile || profileUrl);
          setUserState({
            name: user.name,
            email: user.email,
            mobile: user.mobile,
            profile: user.profile || profileUrl,
            id: user.id,
            role: user.role,
            companyId: user.companyId,
          });
          resolve(user);
        },
        onError: (error) => {
          toast.error(error || t("profile.form.messages.photoUploadError"));
          reject(new Error(error));
        },
      });
    });
  };

  const handleBusinessLogoSave = async (logoUrl) => {
    const values = { ...businessValidation.values, logo: logoUrl };
    const payload = buildBusinessPayload(values);

    await new Promise((resolve, reject) => {
      callApi({
        callback: updateBusinessDetail,
        payload,
        onSuccess: ({ message, businessDetail: updated }) => {
          toast.success(message || t("profile.businessDetail.messages.logoUploadSuccess"));
          const nextDetail = {
            companyName: updated?.companyName || values.companyName,
            email: updated?.email || values.email,
            mobile: updated?.mobile || values.mobile,
            gstNumber: updated?.gstNumber || values.gstNumber,
            panCard: updated?.panCard || values.panCard,
            address: updated?.address || values.address,
            logo: updated?.logo || logoUrl,
            subscriptionId:
              updated?.subscriptionId != null
                ? String(updated.subscriptionId)
                : values.subscriptionId,
            otherDetails: {
              registrationNumber:
                updated?.otherDetails?.registrationNumber ||
                values.otherDetails?.registrationNumber ||
                "",
            },
          };
          setBusinessDetail(nextDetail);
          businessValidation.setFieldValue("logo", nextDetail.logo);
          resolve(updated);
        },
        onError: (error) => {
          toast.error(error || t("profile.businessDetail.messages.logoUploadError"));
          reject(new Error(error));
        },
      });
    });
  };

  return (
    <>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb
            title={PLATFORM_NAME}
            breadcrumbItem={t("profile.page.breadcrumb")}
          />

          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <div className="d-flex align-items-center">
                    <CircularImagePicker
                      value={validation.values.profile}
                      onChange={() => {}}
                      onSave={handleProfilePhotoSave}
                      defaultImage={avatar}
                      size={96}
                      alt={t("profile.form.alt.userAvatar")}
                      onUploadError={() =>
                        toast.error(t("profile.form.messages.photoUploadError"))
                      }
                      {...imageEditorLabels("form")}
                    />
                    <div className="flex-grow-1 align-self-center ms-3">
                      <div className="text-muted">
                        <p className="mb-0">
                          {t("profile.display.idLabel", { id: userState.id })}
                        </p>
                        <h5 className="mb-0">{userState.name}</h5>
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <h4 className="card-title mb-4">{t("profile.form.heading")}</h4>

          <Card>
            <CardBody>
              <Form
                className="form-horizontal"
                onSubmit={(e) => {
                  e.preventDefault();
                  validation.handleSubmit();
                  return false;
                }}
              >
                <Row>
                  <Col md="6">
                    <div className="form-group mb-3">
                      <Label className="form-label required">
                        {t("profile.form.labels.name")}
                      </Label>
                      <Input
                        name="name"
                        className="form-control"
                        placeholder={t("profile.form.placeholders.enterName")}
                        type="text"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.name || ""}
                        invalid={
                          validation.touched.name && validation.errors.name
                            ? true
                            : false
                        }
                      />
                      {validation.touched.name && validation.errors.name ? (
                        <FormFeedback type="invalid">
                          {validation.errors.name}
                        </FormFeedback>
                      ) : null}
                    </div>
                  </Col>

                  <Col md="6">
                    <div className="form-group mb-3">
                      <Label className="form-label required">
                        {t("profile.form.labels.email")}
                      </Label>
                      <Input
                        name="email"
                        className="form-control"
                        placeholder={t("profile.form.placeholders.enterEmail")}
                        type="email"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.email || ""}
                        invalid={
                          validation.touched.email && validation.errors.email
                            ? true
                            : false
                        }
                      />
                      {validation.touched.email && validation.errors.email ? (
                        <FormFeedback type="invalid">
                          {validation.errors.email}
                        </FormFeedback>
                      ) : null}
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col md="6">
                    <div className="form-group mb-3">
                      <Label className="form-label required">
                        {t("profile.form.labels.mobileNumber")}
                      </Label>
                      <CommonMobileInput
                        name="mobile"
                        value={validation.values.mobile || ""}
                        onChange={(val) => validation.setFieldValue("mobile", val)}
                        onBlur={() => validation.setFieldTouched("mobile", true)}
                        touched={validation.touched.mobile}
                        error={validation.errors.mobile}
                        placeholder={t("profile.form.placeholders.enterMobile")}
                      />
                    </div>
                  </Col>

                  <Col md="6">
                    <div className="form-group mb-3">
                      <Label className="form-label">
                        {t("profile.form.labels.userId")}
                      </Label>
                      <Input
                        className="form-control"
                        value={`#${userState.id}`}
                        disabled
                        readOnly
                      />
                    </div>
                  </Col>
                </Row>

                <Input name="id" value={validation.values.id} type="hidden" />

                <div className="text-center mt-4">
                  <Button
                    type="submit"
                    color="primary"
                    size="lg"
                    disabled={!validation.isValid}
                  >
                    <i className="ri-save-line me-2"></i>
                    {t("profile.form.buttons.updateProfile")}
                  </Button>
                </div>
              </Form>
            </CardBody>
          </Card>

          {isDistributor && (
            <>
              <h4 className="card-title mb-4 mt-2">{t("profile.businessDetail.heading")}</h4>

              <Card>
                <CardBody>
                  {businessLoading ? (
                    <div className="text-center py-4">
                      <Spinner color="primary" />
                      <p className="text-muted mt-2 mb-0">
                        {t("profile.businessDetail.loading")}
                      </p>
                    </div>
                  ) : (
                    <Form
                      className="form-horizontal"
                      onSubmit={(e) => {
                        e.preventDefault();
                        businessValidation.handleSubmit();
                        return false;
                      }}
                    >
                      <div className="d-flex align-items-center mb-4 pb-3 border-bottom">
                        <CircularImagePicker
                          value={businessValidation.values.logo}
                          onChange={() => {}}
                          onSave={handleBusinessLogoSave}
                          defaultImage={avatar}
                          size={88}
                          alt={t("profile.businessDetail.alt.companyLogo")}
                          onUploadError={() =>
                            toast.error(t("profile.businessDetail.messages.logoUploadError"))
                          }
                          {...imageEditorLabels("businessDetail")}
                        />
                        <div className="ms-3">
                          <p className="text-muted mb-1 small">
                            {t("profile.businessDetail.labels.logo")}
                          </p>
                          <h5 className="mb-0">
                            {businessValidation.values.companyName ||
                              t("profile.businessDetail.placeholders.shopName")}
                          </h5>
                        </div>
                      </div>

                      <Row>
                        <Col md="6">
                          <div className="form-group mb-3">
                            <Label className="form-label required">
                              {t("profile.businessDetail.labels.shopName")}
                            </Label>
                            <Input
                              name="companyName"
                              className="form-control"
                              placeholder={t("profile.businessDetail.placeholders.shopName")}
                              type="text"
                              onChange={businessValidation.handleChange}
                              onBlur={businessValidation.handleBlur}
                              value={businessValidation.values.companyName || ""}
                              invalid={
                                businessValidation.touched.companyName &&
                                businessValidation.errors.companyName
                              }
                            />
                            {businessValidation.touched.companyName &&
                            businessValidation.errors.companyName ? (
                              <FormFeedback type="invalid">
                                {businessValidation.errors.companyName}
                              </FormFeedback>
                            ) : null}
                          </div>
                        </Col>

                        <Col md="6">
                          <div className="form-group mb-3">
                            <Label className="form-label required">
                              {t("profile.businessDetail.labels.companyEmail")}
                            </Label>
                            <Input
                              name="email"
                              className="form-control"
                              placeholder={t("profile.businessDetail.placeholders.companyEmail")}
                              type="email"
                              onChange={businessValidation.handleChange}
                              onBlur={businessValidation.handleBlur}
                              value={businessValidation.values.email || ""}
                              invalid={
                                businessValidation.touched.email &&
                                businessValidation.errors.email
                              }
                            />
                            {businessValidation.touched.email &&
                            businessValidation.errors.email ? (
                              <FormFeedback type="invalid">
                                {businessValidation.errors.email}
                              </FormFeedback>
                            ) : null}
                          </div>
                        </Col>
                      </Row>

                      <Row>
                        <Col md="6">
                          <div className="form-group mb-3">
                            <Label className="form-label required">
                              {t("profile.businessDetail.labels.companyMobile")}
                            </Label>
                            <CommonMobileInput
                              name="mobile"
                              value={businessValidation.values.mobile || ""}
                              onChange={(val) => businessValidation.setFieldValue("mobile", val)}
                              onBlur={() => businessValidation.setFieldTouched("mobile", true)}
                              touched={businessValidation.touched.mobile}
                              error={businessValidation.errors.mobile}
                              placeholder={t("profile.businessDetail.placeholders.companyMobile")}
                            />
                          </div>
                        </Col>

                        <Col md="6">
                          <div className="form-group mb-3">
                            <Label className="form-label required">
                              {t("profile.businessDetail.labels.gstNumber")}
                            </Label>
                            <Input
                              name="gstNumber"
                              className="form-control"
                              placeholder={t("profile.businessDetail.placeholders.gstNumber")}
                              type="text"
                              onChange={businessValidation.handleChange}
                              onBlur={businessValidation.handleBlur}
                              value={businessValidation.values.gstNumber || ""}
                              invalid={
                                businessValidation.touched.gstNumber &&
                                businessValidation.errors.gstNumber
                              }
                            />
                            {businessValidation.touched.gstNumber &&
                            businessValidation.errors.gstNumber ? (
                              <FormFeedback type="invalid">
                                {businessValidation.errors.gstNumber}
                              </FormFeedback>
                            ) : null}
                          </div>
                        </Col>
                      </Row>

                      <Row>
                        <Col md="6">
                          <div className="form-group mb-3">
                            <Label className="form-label required">
                              {t("profile.businessDetail.labels.panCard")}
                            </Label>
                            <Input
                              name="panCard"
                              className="form-control text-uppercase"
                              placeholder={t("profile.businessDetail.placeholders.panCard")}
                              type="text"
                              maxLength={10}
                              onChange={(e) => {
                                businessValidation.setFieldValue(
                                  "panCard",
                                  e.target.value.toUpperCase()
                                );
                              }}
                              onBlur={businessValidation.handleBlur}
                              value={businessValidation.values.panCard || ""}
                              invalid={
                                businessValidation.touched.panCard &&
                                businessValidation.errors.panCard
                              }
                            />
                            {businessValidation.touched.panCard &&
                            businessValidation.errors.panCard ? (
                              <FormFeedback type="invalid">
                                {businessValidation.errors.panCard}
                              </FormFeedback>
                            ) : null}
                          </div>
                        </Col>

                        <Col md="6">
                          <div className="form-group mb-3">
                            <Label className="form-label">
                              {t("profile.businessDetail.labels.registrationNumber")}
                            </Label>
                            <Input
                              name="otherDetails.registrationNumber"
                              className="form-control"
                              placeholder={t("profile.businessDetail.placeholders.registrationNumber")}
                              type="text"
                              onChange={businessValidation.handleChange}
                              onBlur={businessValidation.handleBlur}
                              value={
                                businessValidation.values.otherDetails?.registrationNumber || ""
                              }
                              invalid={
                                businessValidation.touched.otherDetails?.registrationNumber &&
                                businessValidation.errors.otherDetails?.registrationNumber
                              }
                            />
                          </div>
                        </Col>
                      </Row>

                      <Row>
                        <Col md="6">
                          <div className="form-group mb-3">
                            <Label className="form-label">
                              {t("profile.businessDetail.labels.address")}
                            </Label>
                            <Input
                              name="address"
                              className="form-control"
                              placeholder={t("profile.businessDetail.placeholders.address")}
                              type="textarea"
                              rows="3"
                              onChange={businessValidation.handleChange}
                              onBlur={businessValidation.handleBlur}
                              value={businessValidation.values.address || ""}
                            />
                          </div>
                        </Col>
                      </Row>

                      <div className="text-center mt-4">
                        <Button
                          type="submit"
                          color="primary"
                          size="lg"
                          disabled={!businessValidation.isValid || businessLoading}
                        >
                          <i className="ri-save-line me-2"></i>
                          {t("profile.businessDetail.buttons.updateBusinessDetail")}
                        </Button>
                      </div>
                    </Form>
                  )}
                </CardBody>
              </Card>
            </>
          )}
        </Container>
      </div>
    </>
  );
};

export default withTranslation()(withRouter(UserProfile));
